import {Inject, Injectable} from '@angular/core';
import {ComponentStore} from '@ngrx/component-store';
import {switchMap, tap, withLatestFrom} from 'rxjs/operators';
import {Observable} from 'rxjs';
import {RouteModel} from "../../models/route.model";
import {RoutesHttpService} from "../../../lean-planning/angular/services/routes-http.service";
import {HttpErrorResponse} from "@angular/common/http";
import {RoutesQueryModel} from "../../models/routes-query.model";
import firebase from "firebase/compat/app";
import {onValue, ref} from "firebase/database";
import Unsubscribe = firebase.Unsubscribe;
import {TaskModel} from "../../../tasks/models/task.model";

export interface RoutesComponentState {
  routes: RouteModel[];
  selectedRouteId: string;
  cursor?: string;
  loading: boolean;
  initialized: boolean;
  error: string | null;
}

export interface VmRoutesComponentState {
  routes: RouteModel[];
  selectedRouteId: string;
  cursor?: string;
  loading: boolean;
  initialized: boolean;
  error: string | null;
}

@Injectable()
export class RoutesComponentStore extends ComponentStore<RoutesComponentState> {

  // Selectors
  readonly routes$ = this.select((state) => state.routes);
  readonly selectedRouteId$ = this.select((state) => state.selectedRouteId);
  readonly loading$ = this.select((state) => state.loading);
  readonly initialized$ = this.select((state) => state.initialized);
  readonly error$ = this.select((state) => state.error);
  readonly cursor$ = this.select((state) => state.cursor);
  readonly selectedRoute$ = this.select(
    this.routes$,
    this.selectedRouteId$,
    (routes: RouteModel[], id: string) => routes.find((r: RouteModel) => r.id === id)
  );
  // readonly selectRouteById = (id: string) =>
  //   this.select((state) => state.routes.find((route: RouteModel) => route.id === id));


  vm$ = this.select({
    routes: this.routes$,
    selectedRouteId: this.selectedRouteId$,
    loading: this.loading$,
    initialized: this.initialized$,
    error: this.error$,
    cursor: this.cursor$
  })

  private channelsUnsubscribeMap: Map<string, Unsubscribe> = new Map();
  private tasksChannelsUnsubscribeMap: Map<string, Unsubscribe> = new Map();

  constructor(
    @Inject(RoutesHttpService) private routesHttpService: RoutesHttpService,
    @Inject('firebaseService') private firebaseService
  ) {
    super({
      routes: [],
      selectedRouteId: null,
      cursor: '',
      loading: false,
      initialized: false,
      error: null,
    });
  }

  // Updaters
  readonly onSetRoutes = this.updater((state: RoutesComponentState, value: {
    routes: RouteModel[],
    cursor?: string
  }) => ({
    ...state,
    routes: [...state.routes, ...value.routes],
    cursor: value.cursor,
    loading: false,
    initialized: true,
    error: null,
  }));

  readonly selectRoute = this.updater<string>((state, routeId: string) => ({
    ...state,
    selectedRouteId: routeId,
  }));

  readonly updateRoute = this.updater((state: RoutesComponentState, updatedRoute: Partial<RouteModel> & Pick<RouteModel, 'id'>) => ({
    ...state,
    routes: state.routes.map((route: RouteModel) => {
        if (updatedRoute.id !== route.id) {
          return route;
        }

        const merged: RouteModel = {...route, ...updatedRoute};
        const localUpdatedTime = route.updatedTime || route.updatedAt;
        const incomingUpdatedTime = updatedRoute.updatedTime || updatedRoute.updatedAt;

        if (localUpdatedTime && incomingUpdatedTime && localUpdatedTime > incomingUpdatedTime && updatedRoute.metaData) {
          merged.metaData = route.metaData;
        }

        return merged;
      }
    ),
  }));

  // Updaters
  readonly onResetRoutes = this.updater((state: RoutesComponentState) => ({
    ...state,
    routes: [],
    cursor: null,
    loading: false,
    initialized: false,
    error: null,
  }));

  readonly setLoading = this.updater((state, loading: boolean) => ({
    ...state,
    loading,
  }));

  readonly setError = this.updater((state, error: HttpErrorResponse) => ({
    ...state,
    error: error.message,
    loading: false,
  }));

  // Effect for fetching routes
  readonly getRoutes = this.effect(
    (trigger$: Observable<RoutesQueryModel>) =>
      trigger$.pipe(
        tap(() => this.setLoading(true)), // Set loading state
        withLatestFrom(this.cursor$), // Combine with cursor from the state
        switchMap(([params, cursor]) => {
            //TODO clean subscribed routes
            return this.routesHttpService.getRoutes({...params, cursor: cursor || ''}).pipe(
              tap({
                next: (response) => {

                  //Subscribe to routes
                  response.list?.forEach((route: RouteModel) => {
                    this.subscribe(route.id);
                  })
                  return this.onSetRoutes({routes: response.list, cursor: response.cursor});
                },
                error: (err) => this.setError(err.message || 'Failed to fetch routes'),
              })
            )
          }
        )
      )
  );

  // Effect for fetching routes
  readonly setRoutes = this.effect(
    (trigger$: Observable<RouteModel[]>) =>
      trigger$.pipe(
        tap((routes: RouteModel[]) => {
          //Subscribe to routes
          routes?.forEach((route: RouteModel) => {
            this.subscribe(route.id);
          })
          return this.onSetRoutes({routes: routes});
        })
      )
  );

  readonly resetRoutes = this.effect<void>((trigger$) =>
    trigger$.pipe(
      tap(() => {
        this.unSubscribeAll();
        this.onResetRoutes();
      })
    )
  );

  readonly updateSelectedRouteTask = this.updater<any>((state, updatedTask) => {
    if (!state.selectedRouteId) return state;

    const updatedRoutes = state.routes.map(route => {
      if (route.id !== state.selectedRouteId) return route;

        // const updatedTasks = route.taskListExt.map(task =>
        //   task.id === updatedTask.id ? updatedTask : task
        // );
        //
        // return {
        //   ...route,
        //   taskListExt: updatedTasks
        // };

      const updatedTasks = route.routeTaskList.map(task =>
        task.id === updatedTask.id ? updatedTask : task
      );

      return {
        ...route,
        routeTaskList: updatedTasks
      };
    });

    return {
      ...state,
      routes: updatedRoutes
    };
  });

  public unSubscribeAll(): void {
    for (const [id, channelUnsubscribe] of this.channelsUnsubscribeMap) {
      channelUnsubscribe();
    }
    this.channelsUnsubscribeMap.clear();

    for (const [id, channelUnsubscribe] of this.tasksChannelsUnsubscribeMap) {
      channelUnsubscribe();
    }
    this.tasksChannelsUnsubscribeMap.clear();
  }

  // Convert Firebase `onValue` into an observable
  public subscribe(id: string) {
    const channel = this.firebaseService.channel;
    const database = this.firebaseService.database;

    const path = `${channel.channelId}/${'routes_changed'}/${id}`;
    const dbRef = ref(database, path);

    // Listen for real-time updates
    const unsubscribe = onValue(
      dbRef,
      (snapshot) => {
        const route: RouteModel = snapshot.val();
        if (!!route) {
          this.updateRoute(route);
        }
      }
    );

    // Cleanup when subscription is closed
    this.channelsUnsubscribeMap.set(id, unsubscribe);
  }


  // Convert Firebase `onValue` into an observable
  public subscribeTasks(ids: string[]) {
    const channel = this.firebaseService.channel;
    const database = this.firebaseService.database;
    const tasksPath = `${channel.channelId}/tasks_changed`;

    ids.forEach((id) => {
      const path = `${tasksPath}/${id}`;
      const dbRef = ref(database, path);

      const unsubscribe = onValue(dbRef, (snapshot) => {
        const task: TaskModel = snapshot.val();
        if (!!task) {
          // Update the selected route in the store
          this.updateSelectedRouteTask(task);
        }
      });

      this.tasksChannelsUnsubscribeMap.set(id, unsubscribe);
    });
  }

  public unsubscribeTasks(ids: string[]) {
    if (ids.length) {
      ids.forEach((taskId) => {
        const unsubscribeFn = this.tasksChannelsUnsubscribeMap.get(taskId);
        if (unsubscribeFn) {
          unsubscribeFn();
          this.tasksChannelsUnsubscribeMap.delete(taskId)
        }
      })
    }
  }

}
